import { router } from '@inertiajs/react';
import { Loader2, Search, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import type { AvailableHacker } from '@/types';
import { githubUsername } from '@/components/admin/github-username';

interface AddHackerDialogProps {
  eventSlug: string;
  teamId: string;
  teamSlug: string | null;
  hackerSearch: string;
  availableHackers: AvailableHacker[];
}

// searchHackersAction ran on the server behind a 500ms debounce; here the same query is a
// partial reload of this page, so the results arrive as props instead of as a payload.
export function AddHackerDialog({
  eventSlug,
  teamId,
  teamSlug,
  hackerSearch,
  availableHackers,
}: AddHackerDialogProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(hackerSearch);
  const [isSearching, setIsSearching] = useState(false);
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    if (searchQuery === hackerSearch || !teamSlug) return;

    const delayDebounce = setTimeout(() => {
      router.get(
        `/admin/${eventSlug}/teams/${teamSlug}`,
        searchQuery ? { hackerSearch: searchQuery } : {},
        {
          only: ['availableHackers', 'hackerSearch'],
          preserveState: true,
          preserveScroll: true,
          replace: true,
          onStart: () => setIsSearching(true),
          onFinish: () => setIsSearching(false),
        },
      );
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery, hackerSearch, eventSlug, teamSlug]);

  const handleAddHacker = (hackerProfileId: string, hackerName: string) => {
    setIsPending(true);
    router.post(
      `/admin/${eventSlug}/teams/${teamId}/members`,
      { hackerProfileId },
      {
        preserveScroll: true,
        onSuccess: () => {
          toast({
            type: 'success',
            description: `${hackerName} added to team successfully`,
          });
          setOpen(false);
          setSearchQuery('');
        },
        onError: (errors) => {
          const failure = errors as unknown as Record<
            string,
            string | undefined
          >;
          toast({
            type: 'error',
            description: failure.base ?? 'Failed to add hacker to team',
          });
        },
        onFinish: () => setIsPending(false),
      },
    );
  };

  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <UserPlus className="mr-2 h-4 w-4" />
        Add Hacker
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Hacker to Team</DialogTitle>
            <DialogDescription>
              Search for hackers by name or GitHub username. Only hackers who
              have completed onboarding will appear.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute top-3 left-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or GitHub username..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
              {isSearching && (
                <Loader2 className="absolute top-3 right-3 h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>

            <div className="max-h-[400px] space-y-2 overflow-y-auto">
              {searchQuery.length < 2 ? (
                <div className="py-8 text-center text-muted-foreground text-sm">
                  Enter at least 2 characters to search
                </div>
              ) : availableHackers.length === 0 && !isSearching ? (
                <div className="py-8 text-center text-muted-foreground text-sm">
                  No hackers found matching &quot;{searchQuery}&quot;
                </div>
              ) : (
                availableHackers.map((hacker) => (
                  <div
                    key={hacker.id}
                    className="flex items-center justify-between rounded-lg border p-4 transition-colors hover:bg-muted/50"
                  >
                    <div className="flex flex-1 flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{hacker.fullName}</span>
                        {hacker.currentTeam && (
                          <Badge variant="secondary">
                            In team: {hacker.currentTeam.slug}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-muted-foreground text-sm">
                        <span>{hacker.email}</span>
                        {hacker.github && (
                          <span className="text-blue-600">
                            @{githubUsername(hacker.github)}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() =>
                        handleAddHacker(hacker.id, hacker.fullName)
                      }
                      disabled={isPending}
                    >
                      {isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Add'
                      )}
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
